import bus from "/src/utils/event_bus";
import { newestModified } from "/src/utils/poll_since";
import { useSelectedStore } from "@/stores/selected";
import { useAppStore } from "@/stores/app";
import { useConstructiblesStore } from "@/stores/constructibles";
import { useRefDivergencesStore } from "@/stores/ref_divergences";
import {
    assemblyCountersFromSaveOrClean,
    moduleCountersFromSaveOrClean,
    allCountersFromSaveOrClean,
} from "@/stores/quantities"

// WATCHERS THAT LAUNCH ACTIONS FROM CONSTRUCTIBLES STATE CHANGES

// Designs in one of these analysis states get re-fetched on a timer.
const ANALYSIS_PENDING = ["working", "waiting"]
const PENDING_REFRESH_INTERVAL = 30000 // 30 seconds
let last_pending_designs_signature = null
let pending_refresh_timeout = null

// A design is pending while an analysis for it is queued or running. A design
// that was never queued and has no IFC also reads "waiting", the default the
// backend writes when the key is missing, but no analysis is coming for it:
// it is left out, or it would be re-fetched for as long as the page is open.
function pendingDesignUuids(designs) {
    return designs
        .filter(d => !!d?.uuid
            && ANALYSIS_PENDING.includes(d.meta?.analysis)
            && (d.meta?.has_ifc || d.meta?.analysis_nonce))
        .map(d => d.uuid)
}

// Re-fetches the pending designs every PENDING_REFRESH_INTERVAL for as long as
// any is pending. It used to fire once per change of the pending list, so an
// analysis that outlasted one interval left the list as it was, nothing was
// scheduled again, and the table showed "waiting" or "working" until the page
// was reloaded.
function schedulePendingDesignsRefresh(constructibles_store) {
    if (pending_refresh_timeout) return
    pending_refresh_timeout = setTimeout(() => {
        Promise.allSettled([
            constructibles_store.refreshAssemblyDesigns(
                pendingDesignUuids(constructibles_store.assembly_designs)),
            constructibles_store.refreshModuleDesigns(
                pendingDesignUuids(constructibles_store.module_designs)),
        ]).finally(() => {
            // Cleared only now, so the store updates the refresh itself makes
            // do not start a second timer.
            pending_refresh_timeout = null
            const still_pending =
                pendingDesignUuids(constructibles_store.assembly_designs).length +
                pendingDesignUuids(constructibles_store.module_designs).length
            if (still_pending) schedulePendingDesignsRefresh(constructibles_store)
        })
    }, PENDING_REFRESH_INTERVAL)
}

let BATCH_FIRST_RUN = true
let MODD_FR = true
let ASSD_FR = true

let old_batch_uuids = []
let old_modd_uuids = []
let old_assd_uuids = []
let old_mods_signature = null
let old_asss_signature = null

// Cheap stand-in for a deep compare of the actuals list. Only the fields the
// quantities counters are built from are included, so status/date churn from the
// polling loop does not look like a change.
function actualsSignature(actuals) {
    let signature = `${actuals.length}`
    for (const a of actuals) signature += `|${a.design}>${a.batch}`
    return signature
}

// True when any uuid present on the previous run is gone now.
function hasRemoved(prev_uuids, next_uuids) {
    if (prev_uuids.length === 0) return false
    const next = new Set(next_uuids)
    return prev_uuids.some(uuid => !next.has(uuid))
}

// STATUS POLLING
const POLL_INTERVAL = 15000 // 15 seconds
let poll_timeout = null
let last_poll_timestamp = null



function startStatusPolling(constructibles_store, selected_store, app_store) {
    stopStatusPolling()

    last_poll_timestamp = newestModified(
        [...constructibles_store.assemblies, ...constructibles_store.modules], null)
        || new Date().toISOString()

    function poll() {
        const has_assemblies = constructibles_store.assemblies.length > 0
        const has_modules = constructibles_store.modules.length > 0
        if (!has_assemblies && !has_modules) {
            stopStatusPolling()
            return
        }

        // Build scope params based on current mode
        // We must always scope to project or date — never poll unscoped
        let scope_params = null
        if (app_store.byDateModeActive) {
            const date = selected_store.selected_date
            if (date && date !== "2000-01-01") {
                scope_params = { project: null, scheduled_prod_date: date }
            }
        } else {
            const pr_uuid = selected_store.selected_project?.uuid
            if (pr_uuid && pr_uuid !== "all") {
                scope_params = { project: pr_uuid }
            }
        }

        if (!scope_params) {
            poll_timeout = setTimeout(poll, POLL_INTERVAL)
            return
        }

        const since = last_poll_timestamp

        let promises = []
        if (has_assemblies) promises.push(constructibles_store.pollAssemblies(scope_params, since))
        if (has_modules) promises.push(constructibles_store.pollModules(scope_params, since))

        Promise.all(promises)
            .then(results => {
                // Only if polling was not restarted meanwhile for another scope.
                if (last_poll_timestamp === since)
                    last_poll_timestamp = newestModified(results.flat(), since)
            })
            .finally(() => {
                poll_timeout = setTimeout(poll, POLL_INTERVAL)
            })
    }

    poll_timeout = setTimeout(poll, POLL_INTERVAL)
}

function stopStatusPolling() {
    if (poll_timeout) {
        clearTimeout(poll_timeout)
        poll_timeout = null
    }
    last_poll_timestamp = null
}


export const init_constructibles_watchers = function () {
    const constructibles_store = useConstructiblesStore()
    const selected_store = useSelectedStore()
    const app_store = useAppStore()

    constructibles_store.$subscribe((mutation, state) => {


        // THIS IS TO REFRESH LIVE CONSTRUCTIBLES
        // TODO THIS SHOULD BE IMMEDIATE=TRUE
        // Only the designs still being analysed matter here, so pick them straight
        // from state instead of re-deriving (and deep-comparing) every design on
        // every single mutation of this store.
        const assds_pending = pendingDesignUuids(state.assembly_designs)
        const modds_pending = pendingDesignUuids(state.module_designs)
        const pending_signature = `${assds_pending.join(",")}|${modds_pending.join(",")}`
        if (pending_signature !== last_pending_designs_signature) {
            // The last pending analysis just finished: the project's reference
            // divergences may have changed, and are only final now.
            if (last_pending_designs_signature?.split("|")[0] && assds_pending.length === 0) {
                useRefDivergencesStore().reloadIfLoaded(selected_store.selected_project?.uuid)
            }
            last_pending_designs_signature = pending_signature
        }
        if (assds_pending.length || modds_pending.length)
            schedulePendingDesignsRefresh(constructibles_store)


        // THIS IS FOR QUANTITIES COUNTERS
        // These three only care whether something was REMOVED, so compare uuid
        // lists instead of deep-comparing whole records on every mutation.
        const batch_uuids = state.batches.map(b => b.uuid)
        if (BATCH_FIRST_RUN || hasRemoved(old_batch_uuids, batch_uuids)) {
            allCountersFromSaveOrClean()
            BATCH_FIRST_RUN = false
        }
        old_batch_uuids = batch_uuids //set for next run

        const modd_uuids = state.module_designs.map(d => d.uuid)
        if (MODD_FR || hasRemoved(old_modd_uuids, modd_uuids)) {
            moduleCountersFromSaveOrClean()
            MODD_FR = false
        }
        old_modd_uuids = modd_uuids //set for next run

        const assd_uuids = state.assembly_designs.map(d => d.uuid)
        if (ASSD_FR || hasRemoved(old_assd_uuids, assd_uuids)) {
            assemblyCountersFromSaveOrClean()
            ASSD_FR = false
        }
        old_assd_uuids = assd_uuids //set for next run

        // THIS IS FOR QUANTITIES COUNTERS
        // Counters are derived from (design, batch) only, so compare on that and
        // ignore everything else. A production worker changing a unit's status —
        // which the 15s poll writes into this store — must NOT re-derive the
        // counters, or it silently overwrites the counts the user is typing.
        const modules_signature = actualsSignature(state.modules)
        if (modules_signature !== old_mods_signature) {
            moduleCountersFromSaveOrClean()
            old_mods_signature = modules_signature //set for next run
        }

        const assemblies_signature = actualsSignature(state.assemblies)
        if (assemblies_signature !== old_asss_signature) {
            assemblyCountersFromSaveOrClean()
            old_asss_signature = assemblies_signature //set for next run
        }

        // START/STOP STATUS POLLING based on loaded actuals
        const has_actuals = state.assemblies.length > 0 || state.modules.length > 0
        if (has_actuals && !poll_timeout) {
            startStatusPolling(constructibles_store, selected_store, app_store)
        } else if (!has_actuals && poll_timeout) {
            stopStatusPolling()
        }

    })

    bus.on("events/constructibles/reloadConstructibles", () => {
        let pr_uuid = selected_store.selected_project?.uuid
        constructibles_store.loadAssemblies(pr_uuid)
        constructibles_store.loadModules(pr_uuid)
    })

    bus.on("events/constructible/routeToMyNextActual", () => {
        let first = constructibles_store.myFirst
        console.log("[!] TODO!!!!: push here route to:", first.uuid)
    })

}